import _ from "lodash"
import {migrateConversations} from "./utils"

const organizationTypeByCode = {
    AA: 'association',
    AS: 'association',
    FO: 'supplier',
    IN: 'internal'
}

const newOrganizationTypeIds = new Set(['association', 'supplier', 'partner', 'internal'])

function resolveOldOrganizationTypeCode(organization, organizationTypeCodeById) {
    const rawOrganizationType = organization.organizationType
    if (!rawOrganizationType) return null
    // already migrated to the new static id, nothing to resolve
    if (typeof rawOrganizationType === 'string' && newOrganizationTypeIds.has(rawOrganizationType)) return null
    return organizationTypeCodeById.get(rawOrganizationType.toString()) || null
}

function migrateOrganizationType(organization, organizationTypeCodeById, set) {
    const code = resolveOldOrganizationTypeCode(organization, organizationTypeCodeById)
    if (!code) return

    const newOrganizationType = organizationTypeByCode[code]
    if (newOrganizationType) set.organizationType = newOrganizationType
    if (code === 'AA') set.status = 'member'
}

function migrateCode(organization, set) {
    if (typeof organization.code !== 'string' || !organization.code.endsWith('-OK')) return
    set.code = organization.code.slice(0, -'-OK'.length)
}

function migrateRup(organization, set) {
    if (typeof organization.rup !== 'boolean') return
    set.rup = organization.rup ? 'yes' : 'no'
}

function migrateActivities(organization, shelterTypesByOrganizationId, set) {
    const shelterTypes = shelterTypesByOrganizationId.get(organization._id.toString()) || []
    set.activities = _.uniq([...(organization.activities || []), ...shelterTypes])
}

function migrateAnimalTypes(organization, animalTypesByOrganizationId, set) {
    const animalTypes = animalTypesByOrganizationId.get(organization._id.toString()) || []
    set.animalTypes = _.uniqBy(animalTypes, id => id.toString())
}

function migrateAddress(organization, headquarterShelterByOrganizationId, set) {
    const headquarterShelter = headquarterShelterByOrganizationId.get(organization._id.toString())
    if (!headquarterShelter) return

    set.address = headquarterShelter.address
    set.additionalAddress = headquarterShelter.additionalAddress
    set.zipCode = headquarterShelter.zipCode
    set.city = headquarterShelter.city
}

function buildUpdate(organization, lookups) {
    const {organizationTypeCodeById, shelterTypesByOrganizationId, animalTypesByOrganizationId, headquarterShelterByOrganizationId} = lookups

    const set = {
        country: 'France',
        tradingName: organization.name,
        appellation: organization.address
    }

    migrateOrganizationType(organization, organizationTypeCodeById, set)
    migrateCode(organization, set)
    migrateRup(organization, set)
    migrateActivities(organization, shelterTypesByOrganizationId, set)
    migrateAnimalTypes(organization, animalTypesByOrganizationId, set)
    migrateAddress(organization, headquarterShelterByOrganizationId, set)
    migrateConversations(organization, set)

    return set
}

function buildShelterLookups(shelters) {
    const shelterTypesByOrganizationId = new Map()
    const animalTypesByOrganizationId = new Map()
    const headquarterShelterByOrganizationId = new Map()

    shelters.forEach(shelter => {
        if (!shelter.organization) return
        const organizationId = shelter.organization.toString()

        shelterTypesByOrganizationId.set(
            organizationId,
            _.union(shelterTypesByOrganizationId.get(organizationId) || [], shelter.types || [])
        )

        animalTypesByOrganizationId.set(
            organizationId,
            _.unionBy(animalTypesByOrganizationId.get(organizationId) || [], shelter.animalTypes || [], id => id.toString())
        )

        if (shelter.status === 'headquarters') headquarterShelterByOrganizationId.set(organizationId, shelter)
    })

    return {shelterTypesByOrganizationId, animalTypesByOrganizationId, headquarterShelterByOrganizationId}
}

async function applyJob() {
    const organizationCollection = global.app.C.Organization.collection
    const shelterCollection = global.app.C.Shelter.collection
    const organizationTypeCollection = global.app.C.OrganizationType.collection

    const [organizations, shelters, organizationTypes] = await Promise.all([
        organizationCollection.find({}).toArray(),
        shelterCollection.find({}).toArray(),
        organizationTypeCollection.find({}).toArray()
    ])

    const organizationTypeCodeById = new Map(organizationTypes.map(type => [type._id.toString(), type.code]))
    const {shelterTypesByOrganizationId, animalTypesByOrganizationId, headquarterShelterByOrganizationId} = buildShelterLookups(shelters)

    const operations = organizations.map(organization => ({
        updateOne: {
            filter: {_id: organization._id},
            update: {
                $set: buildUpdate(organization, {organizationTypeCodeById, shelterTypesByOrganizationId, animalTypesByOrganizationId, headquarterShelterByOrganizationId})
            }
        }
    }))

    if (!operations.length) return {message: "Aucune organisation à adapter"}

    const result = await organizationCollection.bulkWrite(operations)
    return {message: `${result.modifiedCount} organisation(s) adaptée(s) sur ${organizations.length}`}
}

export const job = {
    name: "adaptOrganizationCollection",
    title: "Adapter la collection Organization au nouveau modèle",
    execute: function(context, callback) {
        applyJob()
            .then(result => callback(null, result))
            .catch(callback)
    }
}